import { MouseEvent, useEffect, useState } from 'react';
import Box from '@mui/material/Box';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardMedia from '@mui/material/CardMedia';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import KmxArrowRightFilled from '@kmx/mui-icons/KmxArrowRightFilled';

import { getVehiclesTotalCount } from '../../../../../../api/search-api';
import { formatSeperatedInteger } from '../../../../../instant-offers/utils/format';
import { ANALYTICS_CONSTANTS, trackAnchorClick } from '../../../../../../utilities/analytics';
import useAppState from '../../../../../../common/state/appState';

interface IHandleClickParams {
    title: string;
    href: string;
}

const handleClick =
    ({ title, href }: IHandleClickParams) =>
    (e: MouseEvent) => {
        trackAnchorClick({
            clickEvent: e,
            taggingEvent: ANALYTICS_CONSTANTS.EVENTS.CLICK.SEARCH_INITIATED,
            title,
            position: 'shop cars near me',
            href,
            page: 'Homepage',
        });
    };

interface IRelatedSearchesCardProps {
    imgSrc: string;
    title: string;
    subtitle: string;
    href: string;
}

const RelatedSearchesCard = ({ imgSrc, title, subtitle, href }: IRelatedSearchesCardProps) => (
    <Card variant="outlined" sx={{ width: 272, borderRadius: 1.5 }}>
        <CardActionArea href={href} onClick={handleClick({ title, href })}>
            <Stack direction="row" spacing={1.5} sx={{ alignItems: 'center', p: 1, pr: 2, height: 64 }}>
                <CardMedia
                    image={`/home/images/related-searches-tile/related-searches-${imgSrc}`}
                    title={title}
                    sx={{
                        width: '3rem',
                        height: '3rem',
                        borderRadius: 1,
                    }}
                />
                <Stack sx={{ flexGrow: 1 }}>
                    <Typography variant="subtitle1">{title}</Typography>
                    <Typography variant="caption" color="textSecondary">
                        {subtitle}
                    </Typography>
                </Stack>
                <KmxArrowRightFilled color="primary" />
            </Stack>
        </CardActionArea>
    </Card>
);

export const RelatedSearchesTile: React.FC = () => {
    const [testDriveCount, setTestDriveCount] = useState(0);
    const [localPriceDropsCount, setLocalPriceDropsCount] = useState(0);
    const [freeShippingCount, setFreeShippingCount] = useState(0);
    const [allCarsCounts, setAllCarsCounts] = useState(0);
    const storeId = useAppState((state) => state.storeId);

    useEffect(() => {
        (async () => {
            const defaultSearchProps = { storeId, radiusInMiles: 25 };

            const fetchTestDrive = async () => {
                const totalCount = await getVehiclesTotalCount({
                    ...defaultSearchProps,
                    isInStoreOnly: true,
                });
                setTestDriveCount(totalCount);
            };

            const fetchLocalPriceDrops = async () => {
                const totalCount = await getVehiclesTotalCount({
                    ...defaultSearchProps,
                    hasPriceDrop: true,
                });
                setLocalPriceDropsCount(totalCount);
            };

            const fetchFreeShipping = async () => {
                const totalCount = await getVehiclesTotalCount({
                    ...defaultSearchProps,
                    hasFreeShipping: true,
                });
                setFreeShippingCount(totalCount);
            };

            const fetchAll = async () => {
                const totalCount = await getVehiclesTotalCount({});
                setAllCarsCounts(totalCount);
            };

            await Promise.all([fetchAll(), fetchTestDrive(), fetchLocalPriceDrops(), fetchFreeShipping()]);
        })();
    }, [storeId]);

    return (
        <article className="scct--car-tile">
            <div className="carousel__slide">
                <div className="carousel-slide-item">
                    <Box sx={{ width: 270, height: 370, display: 'flex', alignItems: 'center' }}>
                        <Stack direction="column" spacing={1.5}>
                            {testDriveCount > 0 && (
                                <RelatedSearchesCard
                                    imgSrc="1.png"
                                    title="Test drive today"
                                    subtitle={`${testDriveCount} matches`}
                                    href="/cars?distance=25"
                                />
                            )}
                            {localPriceDropsCount > 0 && (
                                <RelatedSearchesCard
                                    imgSrc="2.png"
                                    title="Local price drops"
                                    subtitle={`${localPriceDropsCount} matches`}
                                    href="/cars?distance=25&flag_PriceDrop=true"
                                />
                            )}
                            {freeShippingCount > 0 && (
                                <RelatedSearchesCard
                                    imgSrc="3.png"
                                    title="Free shipping"
                                    subtitle={`${freeShippingCount} matches`}
                                    href="/cars?shipping=0"
                                />
                            )}
                            {allCarsCounts > 0 && (
                                <RelatedSearchesCard
                                    imgSrc="4.png"
                                    title="Shop all cars"
                                    subtitle={`${formatSeperatedInteger(allCarsCounts)} matches`}
                                    href="/cars/all"
                                />
                            )}
                        </Stack>
                    </Box>
                </div>
            </div>
        </article>
    );
};
